import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  AlertTriangle as WarnIcon,
  ArrowRight as NextIcon,
  ArrowLeft as BackIcon,
} from 'lucide-react';
import { useEventEdition } from '../../contexts/EventEditionContext';
import {
  parseMoRaceSplitterCsv,
  buildMoImportPreview,
  importMoResults,
  ParsedTimingRow,
  ImportPreview,
  ImportEntry,
  MissingFromCsv,
  TurklasseEntry,
} from '../../services/moResultsImportService';

const STEPS = ['Upload CSV', 'Review matches', 'Resolve statuses', 'Confirm & import'];

const CLASS_LABEL: Record<string, string> = {
  competition: 'Konkurranse',
  trim: 'Trim',
  tur: 'Tur',
};

const MOResultsImportPanel: React.FC = () => {
  const { event: selectedEvent } = useEventEdition();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedTimingRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Step 3 state: overrides for competition/trim missing from CSV
  const [dnsResolutions, setDnsResolutions] = useState<Record<string, 'DNS' | 'DNF' | null>>({});
  // Step 3 state: status for turklasse entries (default FIN)
  const [turStatuses, setTurStatuses] = useState<Record<string, 'FIN' | 'DNS' | 'DNF'>>({});

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ written: number; deleted: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Step 1 — File upload & parse
  // -------------------------------------------------------------------------

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setCsvFile(file);
    setParseError(null);
    setParsedRows([]);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const text = ev.target?.result as string;
          const rows = parseMoRaceSplitterCsv(text);
          if (rows.length === 0) {
            setParseError('No valid timing rows found in the file.');
          } else {
            setParsedRows(rows);
          }
        } catch (err) {
          setParseError('Parse error: ' + (err as Error).message);
        }
      };
      reader.readAsText(file);
    }
  };

  // -------------------------------------------------------------------------
  // Step 1 → Step 2: build preview
  // -------------------------------------------------------------------------

  const handleBuildPreview = useCallback(async () => {
    if (!selectedEvent?.id || parsedRows.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const p = await buildMoImportPreview(selectedEvent.id, parsedRows);
      setPreview(p);
      // Initialise DNS resolutions (default DNS for competition/trim)
      const dr: Record<string, 'DNS' | 'DNF' | null> = {};
      p.missingFromCsv.forEach((m) => { dr[m.registrationId] = m.status ?? 'DNS'; });
      setDnsResolutions(dr);
      // Initialise turklasse statuses (default FIN)
      const ts: Record<string, 'FIN' | 'DNS' | 'DNF'> = {};
      p.turklasse.forEach((t) => { ts[t.registrationId] = t.status; });
      setTurStatuses(ts);
      setActiveStep(1);
    } catch (err) {
      setPreviewError('Failed to build preview: ' + (err as Error).message);
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedEvent?.id, parsedRows]);

  // -------------------------------------------------------------------------
  // Step 3 helpers
  // -------------------------------------------------------------------------

  const setAllDns = (status: 'DNS' | 'DNF') => {
    if (!preview) return;
    const dr: Record<string, 'DNS' | 'DNF' | null> = {};
    preview.missingFromCsv.forEach((m) => { dr[m.registrationId] = status; });
    setDnsResolutions(dr);
  };

  const setAllTur = (status: 'FIN' | 'DNS' | 'DNF') => {
    if (!preview) return;
    const ts: Record<string, 'FIN' | 'DNS' | 'DNF'> = {};
    preview.turklasse.forEach((t) => { ts[t.registrationId] = status; });
    setTurStatuses(ts);
  };

  // -------------------------------------------------------------------------
  // Step 4 — Build entries and import
  // -------------------------------------------------------------------------

  const buildEntries = (): ImportEntry[] => {
    if (!preview) return [];
    const entries: ImportEntry[] = [];

    // Matched (finished) entries
    for (const m of preview.matched) {
      entries.push({
        registrationId: m.registrationId,
        bibNumber: m.bibNumber,
        firstName: m.firstName,
        lastName: m.lastName,
        gender: m.gender,
        age: m.age,
        representing: m.representing,
        resultClass: m.resultClass,
        timeDisplay: m.timeDisplay,
        timeSeconds: m.timeSeconds,
        status: null,
      });
    }

    // Competition/trim missing from CSV (DNS/DNF)
    for (const m of preview.missingFromCsv) {
      const status = dnsResolutions[m.registrationId] ?? 'DNS';
      if (!status) continue; // skip if admin left blank
      entries.push({
        registrationId: m.registrationId,
        bibNumber: m.bibNumber,
        firstName: m.firstName,
        lastName: m.lastName,
        gender: m.gender,
        age: m.age,
        representing: [],
        resultClass: m.resultClass,
        timeDisplay: null,
        timeSeconds: null,
        status,
      });
    }

    // Turklasse entries
    for (const t of preview.turklasse) {
      const status = turStatuses[t.registrationId] ?? 'FIN';
      entries.push({
        registrationId: t.registrationId,
        bibNumber: t.bibNumber,
        firstName: t.firstName,
        lastName: t.lastName,
        gender: t.gender,
        age: null,
        representing: [],
        resultClass: 'tur',
        timeDisplay: null,
        timeSeconds: null,
        status: status === 'FIN' ? null : status,
      });
    }

    return entries;
  };

  const handleImport = async () => {
    if (!preview || !selectedEvent?.id) return;
    setImporting(true);
    setImportError(null);
    try {
      const entries = buildEntries();
      const result = await importMoResults(
        preview.editionId,
        preview.editionYear,
        entries
      );
      setImportResult(result);
      setActiveStep(4); // done state (past last step)
    } catch (err) {
      setImportError('Import failed: ' + (err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload the RaceSplitter CSV export. The file must contain "Bib Number" and "Total Elapsed Time" columns.
      </Typography>
      <Button
        variant="outlined"
        startIcon={<UploadIcon size={16} />}
        onClick={() => fileInputRef.current?.click()}
      >
        Choose CSV file
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {csvFile && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {csvFile.name}
        </Typography>
      )}
      {parseError && <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>}
      {parsedRows.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Parsed <strong>{parsedRows.length}</strong> timing rows.
          Bib range: {Math.min(...parsedRows.map((r) => r.bibNumber))}–{Math.max(...parsedRows.map((r) => r.bibNumber))}
        </Alert>
      )}
      {previewError && <Alert severity="error" sx={{ mt: 2 }}>{previewError}</Alert>}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          endIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <NextIcon size={16} />}
          onClick={handleBuildPreview}
          disabled={parsedRows.length === 0 || previewLoading}
        >
          {previewLoading ? 'Loading…' : 'Next: Review matches'}
        </Button>
      </Box>
    </Box>
  );

  const renderStep2 = () => {
    if (!preview) return null;
    return (
      <Box>
        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip icon={<CheckIcon size={14} />} label={`${preview.matched.length} matched`} color="success" size="small" />
          {preview.unmatchedBibs.length > 0 && (
            <Chip icon={<WarnIcon size={14} />} label={`${preview.unmatchedBibs.length} unmatched bibs`} color="warning" size="small" />
          )}
          {preview.missingFromCsv.length > 0 && (
            <Chip label={`${preview.missingFromCsv.length} missing from CSV`} color="error" size="small" />
          )}
          <Chip label={`${preview.turklasse.length} turklasse`} color="info" size="small" />
        </Box>

        {/* Unmatched bibs warning */}
        {preview.unmatchedBibs.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Bibs in CSV with no registered participant:</strong>{' '}
            {preview.unmatchedBibs.map((b) => b.bibNumber).join(', ')}
            <br />
            <Typography variant="caption">These rows will not be imported. Assign bib numbers to registrations in the Bib Assignment panel first.</Typography>
          </Alert>
        )}

        {/* Matched entries table */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Matched entries ({preview.matched.length})</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bib</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.matched.map((m) => (
                <TableRow key={m.registrationId}>
                  <TableCell>{m.bibNumber}</TableCell>
                  <TableCell>{m.firstName} {m.lastName}</TableCell>
                  <TableCell><Chip label={CLASS_LABEL[m.resultClass] ?? m.resultClass} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    {m.gender ? (
                      m.gender
                    ) : (
                      <Typography variant="caption" color="error.main">Missing</Typography>
                    )}
                  </TableCell>
                  <TableCell>{m.age ?? <Typography variant="caption" color="text.secondary">?</Typography>}</TableCell>
                  <TableCell>{m.timeDisplay}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {preview.matched.filter((m) => !m.gender).length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {preview.matched.filter((m) => !m.gender).length} competition entries have no gender in their user profile — AGG will not be computed for them.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<BackIcon size={16} />} onClick={() => setActiveStep(0)}>Back</Button>
          <Button variant="contained" endIcon={<NextIcon size={16} />} onClick={() => setActiveStep(2)}>
            Next: Resolve statuses
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStep3 = () => {
    if (!preview) return null;
    return (
      <Box>
        {/* Competition/trim missing from CSV */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Competition / Trim — not in CSV ({preview.missingFromCsv.length})
        </Typography>
        {preview.missingFromCsv.length === 0 ? (
          <Alert severity="success" sx={{ mb: 2 }}>All competition and trim participants have timing data.</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setAllDns('DNS')}>Set all DNS</Button>
              <Button size="small" variant="outlined" onClick={() => setAllDns('DNF')}>Set all DNF</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell>Bib</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.missingFromCsv.map((m: MissingFromCsv) => (
                    <TableRow key={m.registrationId}>
                      <TableCell>{m.firstName} {m.lastName}</TableCell>
                      <TableCell><Chip label={CLASS_LABEL[m.resultClass] ?? m.resultClass} size="small" variant="outlined" /></TableCell>
                      <TableCell>{m.age ?? '?'}</TableCell>
                      <TableCell>{m.bibNumber ?? '—'}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <Select
                            value={dnsResolutions[m.registrationId] ?? 'DNS'}
                            onChange={(e) =>
                              setDnsResolutions((prev) => ({
                                ...prev,
                                [m.registrationId]: e.target.value as 'DNS' | 'DNF',
                              }))
                            }
                          >
                            <MenuItem value="DNS">DNS</MenuItem>
                            <MenuItem value="DNF">DNF</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Turklasse */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Turklasse ({preview.turklasse.length})
        </Typography>
        {preview.turklasse.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>No turklasse participants in this edition.</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setAllTur('FIN')}>Set all FIN</Button>
              <Button size="small" variant="outlined" onClick={() => setAllTur('DNS')}>Set all DNS</Button>
              <Button size="small" variant="outlined" onClick={() => setAllTur('DNF')}>Set all DNF</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Bib</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.turklasse.map((t: TurklasseEntry) => (
                    <TableRow key={t.registrationId}>
                      <TableCell>{t.firstName} {t.lastName}</TableCell>
                      <TableCell>{t.bibNumber ?? '—'}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <Select
                            value={turStatuses[t.registrationId] ?? 'FIN'}
                            onChange={(e) =>
                              setTurStatuses((prev) => ({
                                ...prev,
                                [t.registrationId]: e.target.value as 'FIN' | 'DNS' | 'DNF',
                              }))
                            }
                          >
                            <MenuItem value="FIN">FIN</MenuItem>
                            <MenuItem value="DNS">DNS</MenuItem>
                            <MenuItem value="DNF">DNF</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<BackIcon size={16} />} onClick={() => setActiveStep(1)}>Back</Button>
          <Button variant="contained" endIcon={<NextIcon size={16} />} onClick={() => setActiveStep(3)}>
            Next: Confirm
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStep4 = () => {
    if (!preview) return null;
    const entries = buildEntries();
    const finishers = entries.filter((e) => !e.status);
    const dns = entries.filter((e) => e.status === 'DNS');
    const dnf = entries.filter((e) => e.status === 'DNF');
    const noGender = preview.matched.filter((m) => !m.gender && m.resultClass === 'competition');

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Import summary</Typography>
        <List dense>
          <ListItem><ListItemText primary={`Finishers (with time): ${finishers.length}`} /></ListItem>
          <ListItem><ListItemText primary={`DNS: ${dns.length}`} /></ListItem>
          <ListItem><ListItemText primary={`DNF: ${dnf.length}`} /></ListItem>
          <ListItem><ListItemText primary={`Total entries to write: ${entries.length}`} /></ListItem>
          <ListItem>
            <ListItemText
              primary="resultsStatus will be set to: preliminary"
              secondary={`Edition: ${preview.editionId}`}
            />
          </ListItem>
        </List>

        {noGender.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {noGender.length} competition entry/entries have no gender — AGG ranking will not be available for them.
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          This will <strong>delete all existing results</strong> for {preview.editionId} and replace them with these {entries.length} entries.
        </Alert>

        {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<BackIcon size={16} />} onClick={() => setActiveStep(2)} disabled={importing}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CheckIcon size={16} />}
          >
            {importing ? 'Importing…' : `Import ${entries.length} results`}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDone = () => (
    <Alert severity="success" icon={<CheckIcon />}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Import complete</Typography>
      <Typography variant="body2">
        Wrote <strong>{importResult?.written ?? 0}</strong> result documents
        (replaced {importResult?.deleted ?? 0} previous). resultsStatus set to <strong>preliminary</strong>.
      </Typography>
      <Button
        sx={{ mt: 1 }}
        size="small"
        variant="outlined"
        onClick={() => {
          setActiveStep(0);
          setCsvFile(null);
          setParsedRows([]);
          setPreview(null);
          setImportResult(null);
        }}
      >
        Start over
      </Button>
    </Alert>
  );

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  if (!selectedEvent?.id) {
    return <Alert severity="info">Select an event edition to import results.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Results Import (RaceSplitter) — {selectedEvent.id}
      </Typography>

      {activeStep < 4 && (
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}
      {activeStep === 3 && renderStep4()}
      {activeStep === 4 && renderDone()}
    </Box>
  );
};

export default MOResultsImportPanel;
